import {useEffect, useState} from 'react';
import {Moment} from 'moment';

import {fuzzyMatchSanitized, matchPartsFromSegments} from 'common/util/fuzzy-match';
import {DEFAULT_USER_NAME} from 'common/report/util/profile/types';
import {epochTimestampToProjectTimeZone} from 'common/util/format-property';
import {Translatable} from 'common/i18n';
import {formatDuration} from 'common/util/date';
import {useCurrentProject} from 'common/contexts/react/current-project-context';

import {queryUtils, REPLAY_EVENT} from './query-utils';
import {
  EmbedReplayMessage,
  EmbedReplayPayload,
  isReplayWindowMessage,
  ParentAppMessagePayload,
  ParentAppReplayMessage,
  RecordingEventMetadata,
  RecordingEvents,
  RecordingMetadata,
  ReplayMessage,
  TimeDisplaySetting,
  WindowMessageRecipient,
} from './types';

export function getRelevantReplayEvents(replayEvents: RecordingEventMetadata[]): RecordingEventMetadata[] {
  const existingEventMetadata = replayEvents || [];
  return existingEventMetadata.filter((event) => event.eventName !== REPLAY_EVENT);
}

export function getReplayEventCount(replayEvents: RecordingEventMetadata[]): number {
  return getRelevantReplayEvents(replayEvents).length;
}

export function formatRecordingEventTime(replayTime: number, eventTime: number): string {
  const seconds = new Date((eventTime - replayTime) * 1000);
  return seconds.toISOString().substring(11, 16);
}

export function formatRecordingProgressTimeBar(recordingProgress: number, recordingDuration: number) {
  let progress = new Date(recordingProgress);
  const duration = new Date(recordingDuration);

  if (progress > duration) {
    progress = duration;
  }

  let timeDisplaySetting = TimeDisplaySetting.Hours;
  if (duration.getUTCHours() === 0) {
    if (duration.getUTCMinutes() === 0) {
      timeDisplaySetting = TimeDisplaySetting.Seconds;
    } else {
      timeDisplaySetting = TimeDisplaySetting.Minutes;
    }
  }
  return {
    formattedProgress: formatRecordingProgressTime(progress, timeDisplaySetting),
    formattedDuration: formatRecordingProgressTime(duration, timeDisplaySetting),
  };
}

function formatRecordingProgressTime(date: Date, setting: TimeDisplaySetting) {
  if (isNaN(date.getTime())) {
    return `???`;
  }
  if (setting === TimeDisplaySetting.Hours) {
    return date.toISOString().substring(11, 19);
  } else if (setting === TimeDisplaySetting.Minutes) {
    return date.toISOString().substring(14, 19);
  } else {
    return date.toISOString().substring(15, 19);
  }
}

export function getDisplayName(email: string, name: string, distinctId: string): string {
  if (name && name !== DEFAULT_USER_NAME) {
    return name;
  }

  if (email) {
    return email;
  }

  return distinctId;
}

export function getDisplayTime(secondsFromEpoch: number, timezone: string | null, timeOnly = false): Translatable {
  if (!secondsFromEpoch || !timezone || isNaN(new Date(secondsFromEpoch * 1000).getTime())) {
    return ({t}) => t(`session-replay.unknown-time`);
  }

  return ({formatDate}) =>
    formatDate(epochTimestampToProjectTimeZone(secondsFromEpoch * 1000, timezone) as Moment, {
      month: timeOnly ? undefined : `short`,
      day: timeOnly ? undefined : `numeric`,
      hour: `numeric`,
      minute: `numeric`,
      hour12: true,
      timeZone: timezone || undefined,
    });
}

export function getFormattedDuration(duration: number): Translatable {
  if (!duration) {
    return ({t}) => t(`session-replay.unknown-duration`);
  }

  const formattedDuration = formatDuration(duration, 0);

  return `${formattedDuration.value}${formattedDuration.unit[0]}`;
}

export function getEventCount(eventCount: number, eventsLoading: boolean): Translatable {
  if (eventsLoading) {
    return ({t}) => t(`session-replay.loading-events`);
  }
  return ({t}) => t(`session-replay.events-count`, {count: eventCount});
}

export function useReplayEvents(
  replays: RecordingMetadata[],
  selectedReplay: RecordingMetadata | null,
  initialEvents: RecordingEvents[] | null,
) {
  const [recordingsGeneralEventMetadata, setRecordingsGeneralEventMetadata] = useState<
    Map<string, RecordingEventMetadata[]>
  >(new Map());
  const [recordingsSpecificEventMetadata, setRecordingsSpecificEventMetadata] = useState<
    Map<string, RecordingEventMetadata[]>
  >(new Map());
  const [recordingsInitialEventMetadata, setRecordingsInitialEventMetadata] = useState<
    Map<string, RecordingEventMetadata[]>
  >(new Map());
  const [recordingsEventMetadataLoading, setRecordingsEventMetadataLoading] = useState<boolean>(false);
  const currentProject = useCurrentProject();

  async function fetchEventMetadataForReplays(currentReplays: RecordingMetadata[]) {
    if (currentReplays.length > 0) {
      const queryResult = await queryUtils.fetchEventsForReplays(currentReplays, currentProject.get().value);

      const newRecordingsGeneralEventMetadata = new Map(recordingsGeneralEventMetadata);
      queryResult.forEach((result) => {
        newRecordingsGeneralEventMetadata.set(result.replayId, result.replayEvents);
      });
      setRecordingsGeneralEventMetadata(newRecordingsGeneralEventMetadata);
      setRecordingsEventMetadataLoading(false);
    } else {
      setRecordingsEventMetadataLoading(false);
    }
  }

  async function fetchEventMetadataForReplay(replay: RecordingMetadata) {
    const queryResults = await queryUtils.fetchEventsForReplay(replay, currentProject.get().value);

    const newRecordingsSpecificEventMetadata = new Map(recordingsSpecificEventMetadata);
    newRecordingsSpecificEventMetadata.set(replay.replayId, queryResults.replayEvents);
    setRecordingsSpecificEventMetadata(newRecordingsSpecificEventMetadata);
  }

  function getReplayEventsForId(replayId: string) {
    if (!replayId) {
      return [];
    }

    if (recordingsSpecificEventMetadata.has(replayId)) {
      return recordingsSpecificEventMetadata.get(replayId);
    }

    const eventsToReturn = recordingsGeneralEventMetadata.get(replayId) || [];
    const initialEventsForReplay = recordingsInitialEventMetadata.get(replayId) || [];

    initialEventsForReplay.forEach((event) => {
      if (
        !eventsToReturn.some((existingEvent) => {
          return existingEvent.eventName === event.eventName && existingEvent.eventTime === event.eventTime;
        })
      ) {
        eventsToReturn.push(event);
      }
    });

    return eventsToReturn;
  }

  useEffect(() => {
    if (replays.length > 0) {
      setRecordingsEventMetadataLoading(true);
      fetchEventMetadataForReplays(replays);
    }
  }, [replays]);

  useEffect(() => {
    if (selectedReplay) {
      fetchEventMetadataForReplay(selectedReplay);
    }
  }, [selectedReplay]);

  useEffect(() => {
    if (initialEvents === null) {
      return;
    }

    const newRecordingsInitialEventMetadata = new Map(recordingsInitialEventMetadata);

    initialEvents.forEach((entry) => {
      newRecordingsInitialEventMetadata.set(entry.replayId, entry.replayEvents);
    });
    setRecordingsInitialEventMetadata(newRecordingsInitialEventMetadata);
  }, [initialEvents]);

  return {recordingsEventMetadataLoading, getReplayEventsForId};
}

export function getSearchHighlightPieces(
  inputText: string,
  searchFilter: string,
): Array<{text: string; highlight: boolean}> {
  if (!inputText) return [{text: inputText, highlight: false}];

  const match = fuzzyMatchSanitized(inputText.toLowerCase(), searchFilter.toLowerCase());
  if (searchFilter && match) {
    const matchParts = matchPartsFromSegments(inputText, match.segments);

    return matchParts.map((value, i) => {
      return {text: value, highlight: i % 2 === 0};
    });
  } else {
    return [{text: inputText, highlight: false}];
  }
}

export function sendEmbedReplayMessage(payload: EmbedReplayPayload, win: Window) {
  const message: EmbedReplayMessage = {payload, recipient: WindowMessageRecipient.ChildReplay};

  let origin;
  if (REPLAY_FROM_SAME_ORIGIN) {
    // for development environments, both embedded and parent origin should be the devbox
    origin = window.location.origin;
  } else {
    // otherwise, the origin is our CDN
    origin = `https://cdn.mxpnl.com`;
  }

  win.postMessage(message, origin);
}

// targetOrigin here is a variable since postMessage won't support multiple origins, and we need to be able to
// send messages to eu.mixpanel.com, staging-1.mixpanel.com etc.
// the EmbeddedReplay component won't be emitting anything senstive, but for best practice we'll let the parent app
// send down the current origin, and the child will check that against a whitelist and use that.
export function sendParentReplayMessage(payload: ParentAppMessagePayload, targetOrigin: string, win: Window) {
  const message: ParentAppReplayMessage = {payload, recipient: WindowMessageRecipient.ParentApp};

  let origin = targetOrigin;
  if (REPLAY_FROM_SAME_ORIGIN) {
    // for development environments, both embedded and parent origin should be the devbox
    origin = window.location.origin;
  }

  win.postMessage(message, origin);
}

export function useReplayWindowMessageListener<MessageType extends ReplayMessage>(
  recipient: MessageType['recipient'],
  win: Window,
  cb: (message: MessageType) => void,
) {
  useEffect(() => {
    function handleMessage(event: MessageEvent<MessageType>) {
      if (isReplayWindowMessage(event) && event.data.recipient === recipient) {
        cb(event.data);
      }
    }

    win.addEventListener(`message`, handleMessage);

    return () => {
      win.removeEventListener(`message`, handleMessage);
    };
  });
}
